import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiFilter, FiDownload, FiCheck, FiX, FiEdit3, FiInfo, FiAlertTriangle, FiActivity } = FiIcons;

const MasterActivity = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('approvals');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (user) fetchLogs();
  }, [user, activeView]);

  const fetchLogs = async () => {
    setLoading(true);
    let query = supabase
      .from(TABLES.LOGS)
      .select(`*, pilot:pilot_id(username), item:item_id(name, category), char:character_id(name)`)
      .eq('master_id', user.id)
      .order('created_at', { ascending: false });

    if (activeView === 'approvals') {
      query = query.in('status', ['pending_master', 'contested']);
    } else {
      query = query.in('status', ['unpaid', 'paid', 'pending_pilot']);
    }

    const { data } = await query;
    setLogs(data || []);
    setLoading(false);
  };

  const handleReview = async (log, action) => {
    let price = log.value_at_log;
    let reason = '';

    if (action === 'adjust') {
      const newPrice = prompt("Enter adjusted price (₱):", log.value_at_log);
      if (newPrice === null) return;
      price = parseFloat(newPrice);
      reason = prompt("Reason for adjustment:");
      if (!reason) return;
    }

    setProcessingId(log.id);
    try {
      const { error } = await supabase
        .from(TABLES.LOGS)
        .update({
          status: 'pending_pilot',
          master_adjustment_price: price,
          master_adjustment_reason: reason,
          value_at_log: price
        })
        .eq('id', log.id);

      if (error) throw error;
      fetchLogs();
      alert('Review sent to pilot!');
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleQuickApprove = async (logId) => {
    setProcessingId(logId);
    try {
      const { error } = await supabase
        .from(TABLES.LOGS)
        .update({ status: 'unpaid' }) // Quick approve bypasses pilot acceptance if master agrees to original price
        .eq('id', logId);
      
      if (error) throw error;
      fetchLogs();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-12">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tight">Guild Ledger</h2>
          <p className="text-gray-400 font-bold mt-2">Manage approvals and historical transactions.</p>
        </div>
        <div className="flex bg-gray-200/50 p-1.5 rounded-[1.5rem] backdrop-blur-md border border-white">
          <button onClick={() => setActiveView('approvals')} className={`px-6 py-3 rounded-2xl font-black text-xs transition-all ${activeView === 'approvals' ? 'bg-white shadow-xl text-blue-600' : 'text-gray-500'}`}>Pending Approvals</button>
          <button onClick={() => setActiveView('history')} className={`px-6 py-3 rounded-2xl font-black text-xs transition-all ${activeView === 'history' ? 'bg-white shadow-xl text-blue-600' : 'text-gray-500'}`}>Transaction History</button>
        </div>
      </header>

      {activeView === 'approvals' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {logs.map(log => (
            <div key={log.id} className="bento-card border-l-4 border-l-blue-500 relative overflow-hidden">
              {log.is_cap_exemption_request && (
                <div className="absolute top-0 right-0 px-4 py-1.5 bg-amber-500 text-white text-[9px] font-black uppercase rounded-bl-2xl flex items-center gap-1 shadow-lg">
                  <FiAlertTriangle /> Cap Exceeded
                </div>
              )}
              
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gray-900 text-white rounded-2xl flex items-center justify-center shadow-xl relative overflow-hidden">
                    {log.proof_url ? <img src={log.proof_url} className="w-full h-full object-cover" /> : <FiActivity />}
                  </div>
                  <div>
                    <h4 className="font-black text-xl text-gray-900">{log.item?.name}</h4>
                    <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">{log.pilot?.username} &bull; {log.char?.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-gray-900">₱{log.value_at_log?.toLocaleString()}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Qty: {log.quantity}</p>
                </div>
              </div>

              {log.status === 'contested' && (
                <div className="mb-6 p-4 bg-red-50 rounded-2xl border border-red-100">
                  <p className="text-[10px] font-black text-red-400 uppercase tracking-widest flex items-center gap-2">
                    <FiInfo /> Pilot Contested Price
                  </p>
                  <p className="text-xs text-red-600 mt-1 font-bold italic">"{log.pilot_response || 'No reason provided'}"</p>
                </div>
              )}

              <div className="flex gap-3">
                <button 
                  onClick={() => handleQuickApprove(log.id)}
                  disabled={processingId === log.id}
                  className="flex-1 apple-button-primary !py-4 text-xs !bg-emerald-600 shadow-emerald-100 flex items-center justify-center gap-2"
                >
                  <FiCheck /> Quick Approve
                </button>
                <button 
                  onClick={() => handleReview(log, 'adjust')}
                  disabled={processingId === log.id}
                  className="flex-1 apple-button-secondary !py-4 text-xs flex items-center justify-center gap-2"
                >
                  <FiEdit3 /> Adjust & Review
                </button>
              </div>
            </div>
          ))}
          {logs.length === 0 && (
            <div className="md:col-span-2 py-24 text-center bento-card border-dashed">
              <FiCheck className="mx-auto text-emerald-500 text-4xl mb-4" />
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">All drops are cleared!</p>
            </div>
          )}
        </div>
      )}

      {activeView === 'history' && (
        <div className="bento-card !p-0 overflow-hidden animate-in fade-in duration-700">
          <div className="px-8 py-6 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-4 bg-white px-5 py-2.5 rounded-2xl border border-gray-200">
              <FiSearch className="text-gray-400" />
              <input className="outline-none text-sm font-medium w-64 bg-transparent" placeholder="Search entries..." />
            </div>
            <button className="apple-button-secondary !py-2.5 !px-5 text-xs flex items-center gap-2">
              <FiDownload /> Export Ledger
            </button>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left">
              <thead className="bg-[#f5f5f7] border-b border-gray-100">
                <tr>
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Pilot</th>
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Item</th>
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Settled Value</th>
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="font-black text-gray-900">{log.pilot?.username}</div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase">{log.char?.name}</div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="font-bold text-gray-700">{log.item?.name}</div>
                      <div className="text-[10px] text-gray-300 font-medium">{new Date(log.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="px-8 py-6 font-black text-right text-lg text-blue-600">₱{log.value_at_log?.toLocaleString()}</td>
                    <td className="px-8 py-6 text-center">
                      <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-tighter ${log.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterActivity;